import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback, useContext } from 'react';
import { useChatbotStore } from '../../store/chatbot.store';
import { useCollectionCache } from '../../services/collectionCache.service';
import { FirebaseContext } from '../../components/Firebase';
import { createAddTrackPrompt } from '../../prompts/addTrackPrompt';
import { callGeminiAPI } from '../../services/chatbotSharedService';
import ChatMessage from './ChatMessage';
import ChatbotButton from './ChatbotButton';
import './chatbot.scss';
import { mapKeywordsToSelectors } from '../../services/keywordExtractor';
import SecondaryFilterSelection from './SecondaryFilterSelection';
import { audioAnalysisService } from '../../services/audioAnalysisService';
import { youtubeAudioExtractor } from '../../services/youtubeAudioExtractor';

interface AddTrackChatbotProps {
  onApplyMetadata: (data: Record<string, any>) => void;
  currentMetadata: {
    genres: string[];
    tags: string[];
    trackName: string;
    uploadMode?: string;
  };
  onApplyTrackNameToFirstPair?: (name: string) => void;
  onApplyTrackNameToBatchPair?: (name: string) => void;
  onAudioAnalysisStateChange?: (isAnalyzing: boolean) => void;
  onAudioDescriptionReady?: (description: string) => void;
  useSimplePrompts?: boolean;
}

// Context-specific parser for Add a Track Gemini output
function parseAddTrackJson(json: any) {
  // Expect a full metadata object with a top-level keywords array
  if (!json || typeof json !== 'object') return {};
  // Extract keywords and all other fields
  const { keywords = [], ...metadata } = json;
  return { ...metadata, keywords };
}

// Validation function to filter out non-existent tags/genres from Gemini's response
function validateGeminiResponse(parsed: any, collectionCache: any): any {
  // Create a copy to avoid mutating the original
  const validated = JSON.parse(JSON.stringify(parsed));
  
  // Get available collection terms
  const availableTags = new Set(
    collectionCache.tags?.map((item: any) => item.displayName) || []
  );
  const availableGenres = new Set(
    collectionCache.genres?.map((item: any) => item.displayName) || []
  );
  
  // Initialize keywords array if it doesn't exist
  if (!validated.keywords) {
    validated.keywords = [];
  }
  
  // Initialize genres array if it doesn't exist
  if (!validated.genres) {
    validated.genres = [];
  }
  
  // Validate tags array - also check if misplaced genres
  const invalidTags: string[] = [];
  const tagsMovedToGenres: string[] = [];
  if (Array.isArray(validated.tags)) {
    const validTags: string[] = [];
    validated.tags.forEach((tag: string) => {
      if (availableTags.has(tag)) {
        validTags.push(tag);
      } else if (availableGenres.has(tag)) {
        // This "tag" is actually a genre - move it to genres
        if (!validated.genres.includes(tag)) {
          validated.genres.push(tag);
          tagsMovedToGenres.push(tag);
        }
      } else {
        // Add invalid tag to keywords array to preserve the intent
        if (!validated.keywords.includes(tag)) {
          validated.keywords.push(tag);
          invalidTags.push(tag);
        }
      }
    });
    validated.tags = validTags;
  }
  
  // Validate genres array - also check if misplaced tags
  const invalidGenres: string[] = [];
  const genresMovedToTags: string[] = [];
  if (Array.isArray(validated.genres)) {
    const validGenres: string[] = [];
    validated.genres.forEach((genre: string) => {
      if (availableGenres.has(genre)) {
        validGenres.push(genre);
      } else if (availableTags.has(genre)) {
        // This "genre" is actually a tag - move it to tags
        if (!validated.tags.includes(genre)) {
          validated.tags.push(genre);
          genresMovedToTags.push(genre);
        }
      } else {
        // Add invalid genre to keywords array to preserve the intent
        if (!validated.keywords.includes(genre)) {
          validated.keywords.push(genre);
          invalidGenres.push(genre);
        }
      }
    });
    validated.genres = validGenres;
  }
  
  
  return validated;
}

// Utility to convert all keys of an object to lowercase
function toLowercaseKeys(obj: Record<string, any>): Record<string, any> {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v])
  );
}

// Utility to merge and deduplicate (case-insensitive)
function mergeUniqueNames(existing: string[], newNames: string[]): string[] {
  const lowerSet = new Set(existing.map(n => n.toLowerCase()));
  const result = [...existing];
  newNames.forEach(name => {
    if (typeof name === 'string' && name.trim() && !lowerSet.has(name.toLowerCase())) {
      lowerSet.add(name.toLowerCase());
      result.push(name);
    }
  });
  return result;
}

const AddTrackChatbot = forwardRef<{ handleAudioUpload: (file: File) => Promise<void>, applyAudioDescription: (description: string) => Promise<void> }, AddTrackChatbotProps>(({ onApplyMetadata, currentMetadata, onApplyTrackNameToFirstPair, onApplyTrackNameToBatchPair, onAudioAnalysisStateChange, onAudioDescriptionReady, useSimplePrompts = false }, ref) => {
  const { isOpen, addMessage, messages, clearChat, config, fetchConfig } = useChatbotStore();
  const firebase = useContext(FirebaseContext);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const collectionCache = useCollectionCache();
  const [ambiguousMatches, setAmbiguousMatches] = useState<any[]>([]);
  const [showAmbiguousSelection, setShowAmbiguousSelection] = useState(false);
  const [pendingSingleMatches, setPendingSingleMatches] = useState<any[]>([]);
  const [trackNameOptions, setTrackNameOptions] = useState<any[]>([]);
  const [showTrackNameSelection, setShowTrackNameSelection] = useState(false);
  // Store all supplied tracknameoptions in state for prompt context
  const allTrackNameOptionsRef = useRef<string[]>([]);
  const [selectedTrackNameOptions, setSelectedTrackNameOptions] = useState<string[]>([]);
  // Add a ref for the textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Audio analysis state
  const [audioAnalyzing, setAudioAnalyzing] = useState(false);
  // const [audioFile, setAudioFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Always scroll to bottom when messages, ambiguous selection UI, or chatbot open state changes
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, showAmbiguousSelection, isOpen]);

  // Focus input when chatbot is opened or ambiguous selection is resolved
  useEffect(() => {
    if (isOpen && !showAmbiguousSelection && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, showAmbiguousSelection]);

  // Clear chat history when navigating to this page (component mount)
  useEffect(() => {
    clearChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch config on mount
  useEffect(() => {
    if (!config && firebase) {
      fetchConfig(firebase);
    }
  }, [config, fetchConfig, firebase]);

  // Auto-resize textarea as user types (like ChatbotPanel)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Focus textarea when chatbot is opened
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Handle audio file upload
  const handleAudioUpload = useCallback(async (file: File) => {

    if (!config?.apiKey || !config.enabled) {
      addMessage({ sender: 'bot', message: 'Audio analysis is not available. Please check your configuration.' });
      return;
    }

    setAudioAnalyzing(true);
    onAudioAnalysisStateChange?.(true);
    
    addMessage({ 
      sender: 'user', 
      message: `📁 Uploaded audio file: ${file.name} (${audioAnalysisService.formatFileSize(file.size)})` 
    });
    addMessage({ sender: 'bot', message: '🎵 Analyzing your audio file...' });

    try {
      // Prepare collection data for analysis with categorized tags
      const { categorizeTags } = await import('../../utils/tagCategorizer');
      const allTags = collectionCache.tags.map((t: any) => t.displayName);
      const categorizedTags = categorizeTags(allTags);
      
      const collectionData = {
        genres: collectionCache.genres.map((g: any) => ({ displayName: g.displayName })),
        tags: collectionCache.tags.map((t: any) => ({ displayName: t.displayName })),
        categorizedTags
      };

      const analysisResult = await audioAnalysisService.analyzeAudioFile(
        file, 
        config.apiKey, 
        'add',
        collectionData,
        useSimplePrompts
      );

      // Remove the analyzing message
      useChatbotStore.setState(state => ({ messages: state.messages.slice(0, -1) }));

      // Add the analysis result as a bot message
      addMessage({ 
        sender: 'bot', 
        message: `I've analyzed your audio file: ${analysisResult.generatedQuery}` 
      });

      // Phase 1 complete - notify parent with the description
      onAudioDescriptionReady?.(analysisResult.generatedQuery);
      
      // Don't automatically process - wait for user to click Apply
      
    } catch (error) {
      console.error('Error analyzing audio:', error);
      useChatbotStore.setState(state => ({ messages: state.messages.slice(0, -1) }));
      addMessage({ 
        sender: 'bot', 
        message: `❌ Error analyzing audio: ${error instanceof Error ? error.message : 'Unknown error'}` 
      });
    } finally {
      setAudioAnalyzing(false);
      onAudioAnalysisStateChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.apiKey, config?.enabled, addMessage]);

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleAudioUpload(file);
    }
    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle YouTube URL detection and processing
  const detectAndProcessYouTubeUrl = async (text: string) => {
    const youtubeInfo = youtubeAudioExtractor.detectYouTubeUrl(text);
    if (youtubeInfo) {
      addMessage({ 
        sender: 'bot', 
        message: `🎬 I detected a YouTube URL. YouTube audio extraction is not yet implemented. Please upload an audio file directly for now.` 
      });
      return true;
    }
    return false;
  };

  // Process a generated metadata description through the existing addTrack pipeline
  const processGeneratedMetadata = useCallback(async (queryText: string) => {
    if (!queryText.trim() || !config?.apiKey || !config.enabled || !collectionCache.isInitialized) return;
    
    setLoading(true);
    try {
      // Prepare collection data for the prompt
      const collectionData = {
        genres: collectionCache.genres.map((g: any) => ({ displayname: g.displayName })),
        tags: collectionCache.tags.map((t: any) => ({ displayname: t.displayName })),
      };
      // Prepare current metadata, always include up-to-date tracknameoptions
      const metaWithTrackNameOptions = {
        ...toLowercaseKeys(currentMetadata),
        tracknameoptions: allTrackNameOptionsRef.current,
      };
      // Prepare full chat history (all messages)
      const fullChatHistory = messages.map(m => ({ sender: m.sender, message: m.message }));
      const prompt = createAddTrackPrompt(queryText, collectionData, metaWithTrackNameOptions, fullChatHistory);
      addMessage({ sender: 'bot', message: 'Analyzing your description...' });
      const geminiResult = await callGeminiAPI(prompt, config.apiKey);
      // Remove the 'Analyzing...' message
      useChatbotStore.setState(state => ({ messages: state.messages.slice(0, -1) }));
      // Parse Gemini's JSON output for Add a Track context
      const parsed = parseAddTrackJson(geminiResult.json);
      // Validate the parsed response against collection cache
      const validatedParsed = validateGeminiResponse(parsed, collectionCache);
      // --- TRACK NAME OPTIONS HANDLING ---
      // Get all existing track names (case-insensitive) from collection cache
      const allTrackNames = (collectionCache.allTrackNames || []).map((n: string) => n.toLowerCase());
      // Accumulate all unique track name suggestions from Gemini
      let autoAppliedTrackName: string | undefined = undefined;
      if (Array.isArray(validatedParsed.tracknameoptions)) {
        // Filter out the current Track Name selector text (e.g., 'Batch Mode' or the current trackName)
        const filteredSuggestions = validatedParsed.tracknameoptions.filter(
          (name: string) => name !== currentMetadata.trackName && name !== 'Batch Mode'
        );
        // Merge all Gemini suggestions into the stored list (deduplicated)
        allTrackNameOptionsRef.current = mergeUniqueNames(allTrackNameOptionsRef.current, filteredSuggestions);
        // Now compare only the new Gemini options to the collection's track names
        const uniqueOptions = filteredSuggestions.filter(
          (name: string) => !allTrackNames.includes(name.toLowerCase())
        );
        if (uniqueOptions.length > 0) {
          setTrackNameOptions(uniqueOptions.map((name: string, idx: number) => ({
            id: `${name}-${idx}`,
            label: name,
            collection: 'Track Name Option',
            item: { displayName: name }
          })));
          setShowTrackNameSelection(true);
          addMessage({
            sender: 'bot',
            message: 'Here are some creative track name options. Please select one:',
          });
          setLoading(false);
          return;
        }
      }
      // Also support trackname as an array (legacy/fallback)
      if (Array.isArray(validatedParsed.trackname)) {
        const filteredOptions = validatedParsed.trackname.filter((name: string) =>
          !allTrackNames.includes(name.toLowerCase())
        );
        if (filteredOptions.length > 1) {
          setTrackNameOptions(filteredOptions.map((name: string, idx: number) => ({
            id: `${name}-${idx}`,
            label: name,
            collection: 'Track Name Option',
            item: { displayName: name }
          })));
          setShowTrackNameSelection(true);
          addMessage({
            sender: 'bot',
            message: 'Here are some creative track name options. Please select one:',
          });
          setLoading(false);
          return;
        } else if (filteredOptions.length === 1) {
          autoAppliedTrackName = filteredOptions[0];
          validatedParsed.trackname = filteredOptions[0];
        }
      }
      // If trackname is a single string, merge it as well
      if (typeof validatedParsed.trackname === 'string' && validatedParsed.trackname.trim()) {
        allTrackNameOptionsRef.current = mergeUniqueNames(allTrackNameOptionsRef.current, [validatedParsed.trackname]);
      }
      // Show Gemini's natural language explanation
      addMessage({ sender: 'bot', message: geminiResult.naturalLanguage });
      // Map keywords to selectors using the collection cache
      const { matches } = mapKeywordsToSelectors(validatedParsed.keywords || [], collectionCache);
      // --- AMBIGUOUS MATCH HANDLING ---
      // Build a reverse map: keyword -> [{field, item}]
      const keywordToMatches: Record<string, { field: string, item: any }[]> = {};
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const keyword of validatedParsed.keywords || []) {
        keywordToMatches[keyword] = [];
        for (const [field, items] of Object.entries(matches)) {
          for (const item of items) {
            if (String(item.displayName).toLowerCase().includes(keyword.toLowerCase())) {
              keywordToMatches[keyword].push({ field, item });
            }
          }
        }
      }
      const ambiguous: any[] = [];
      const singles: any[] = [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [keyword, matchList] of Object.entries(keywordToMatches)) {
        if (matchList.length > 1) {
          // ambiguous across fields
          matchList.forEach(({ field, item }) => {
            ambiguous.push({ label: item.displayName, collection: field, item });
          });
        } else if (matchList.length === 1) {
          const { field, item } = matchList[0];
          singles.push({ label: item.displayName, collection: field, item });
        }
      }
      if (ambiguous.length > 0) {
        setAmbiguousMatches(ambiguous);
        setPendingSingleMatches(singles);
        setShowAmbiguousSelection(true);
        addMessage({
          sender: 'bot',
          message: `I found multiple matches for some keywords. Please select which ones you'd like to include:`,
        });
        setLoading(false);
        return;
      }
      // Merge mapped selectors into parsed metadata (fields like genres, tags, etc.)
      const merged = { ...validatedParsed };
      // Add 'composer' (singular) to alwaysArrayFields to ensure both 'composer' and 'composers' are treated as multi-selectors
      const alwaysArrayFields = ['composers', 'composer', 'sessionartists', 'tags', 'genres', 'creativedirector', 'flightingplatform'];
      Object.entries(matches).forEach(([field, items]) => {
        if (items.length) {
          if (alwaysArrayFields.includes(field.toLowerCase())) {
            // Merge Gemini's array (if present) with matched items, dedupe (case-insensitive)
            const geminiArray = Array.isArray(validatedParsed[field]) ? validatedParsed[field] : [];
            const matchedArray = items.map(item => item.displayName);
            const lowerSet = new Set((geminiArray as string[]).map((v: string) => v.toLowerCase()));
            const mergedArray = [...geminiArray];
            matchedArray.forEach((v: string) => {
              if (!lowerSet.has(v.toLowerCase())) {
                lowerSet.add(v.toLowerCase());
                mergedArray.push(v);
              }
            });
            merged[field] = mergedArray;
          } else {
            merged[field] = items[0]?.displayName;
          }
        }
      });
      // --- FIELD NAME NORMALIZATION ---
      const fieldMap: Record<string, string> = {
        sessionartists: 'sessionartists',
        composers: 'composers',
        composer: 'composers', // ensure both singular and plural map to 'composers'
        tags: 'tags',
        genres: 'genres',
        creativedirector: 'creativedirector',
        flightingplatform: 'flightingplatform',
        // add more mappings as needed
      };
      const multiFields = ['composers', 'sessionartists', 'tags', 'genres', 'creativedirector', 'flightingplatform'];
      const normalizedMerged: Record<string, any> = {};
      Object.entries(merged).forEach(([key, value]) => {
        const mappedKey = fieldMap[key.toLowerCase()] || key.toLowerCase();
        if (multiFields.includes(mappedKey) && Array.isArray(value)) {
          if (!normalizedMerged[mappedKey]) {
            normalizedMerged[mappedKey] = [];
          }
          // Merge and dedupe (case-insensitive)
          const lowerSet = new Set(normalizedMerged[mappedKey].map((v: string) => v.toLowerCase()));
          value.forEach((v: string) => {
            if (!lowerSet.has(v.toLowerCase())) {
              lowerSet.add(v.toLowerCase());
              normalizedMerged[mappedKey].push(v);
            }
          });
        } else {
          normalizedMerged[mappedKey] = value;
        }
      });
      // If trackname is present and was auto-applied, update Track Name selector field in parent
      if (autoAppliedTrackName) {
        onApplyMetadata({ trackname: autoAppliedTrackName });
        delete normalizedMerged.trackname;
      }
      onApplyMetadata(normalizedMerged);
    } catch (error: any) {
      addMessage({ sender: 'bot', message: error.message || 'An error occurred.' });
    } finally {
      setLoading(false);
      // Focus the textarea after processing
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.apiKey, config?.enabled, collectionCache.isInitialized, collectionCache.genres, collectionCache.tags, collectionCache.allTrackNames, currentMetadata, messages, onApplyMetadata, onApplyTrackNameToFirstPair, onApplyTrackNameToBatchPair, addMessage, collectionCache]);

  // Apply audio description (Phase 2 processing)
  const applyAudioDescription = useCallback(async (description: string) => {
    await processGeneratedMetadata(description);
  }, [processGeneratedMetadata]);

  // Expose methods to parent through ref
  useImperativeHandle(ref, () => ({
    handleAudioUpload,
    applyAudioDescription
  }), [handleAudioUpload, applyAudioDescription]);

  const handleSend = async () => {
    if (!input.trim() || !config?.apiKey || !config.enabled || !collectionCache.isInitialized) return;
    
    // Check for YouTube URLs first
    const hasYouTubeUrl = await detectAndProcessYouTubeUrl(input);
    if (hasYouTubeUrl) {
      setInput('');
      return;
    }
    
    setLoading(true);
    
    
    const userMessage = input.trim();
    setInput('');
    addMessage({ sender: 'user', message: userMessage });
    
    // Process the query through the existing pipeline
    await processGeneratedMetadata(userMessage);
  };

  // Handler for applying ambiguous matches
  const handleAmbiguousApply = (selected: any[]) => {
    // Merge selected ambiguous matches and pending singles into metadata
    const merged: Record<string, any> = { ...currentMetadata };
    const alwaysArrayFields = ['composers', 'sessionartists', 'tags', 'genres', 'creativedirector', 'flightingplatform'];
    // Group by collection/field
    const grouped: Record<string, any[]> = {};
    [...selected, ...pendingSingleMatches].forEach(match => {
      if (!grouped[match.collection]) grouped[match.collection] = [];
      grouped[match.collection].push(match.item.displayName);
    });
    Object.entries(grouped).forEach(([field, items]) => {
      if (alwaysArrayFields.includes(field.toLowerCase())) {
        merged[field] = items;
      } else {
        merged[field] = items[0];
      }
    });
    onApplyMetadata(merged);
    addMessage({
      sender: 'bot',
      message: `Applied: ${selected.map(m => m.label).join(', ')}`,
    });
    setShowAmbiguousSelection(false);
    setAmbiguousMatches([]);
    setPendingSingleMatches([]);
  };

  const handleAmbiguousDiscard = () => {
    addMessage({ sender: 'bot', message: 'No ambiguous matches were applied.' });
    setShowAmbiguousSelection(false);
    setAmbiguousMatches([]);
    setPendingSingleMatches([]);
  };

  // Handler for applying track name selection
  const handleTrackNameApply = (selected: any[]) => {
    if (selected.length > 0) {
      const chosenName = selected[0].label;
      // If batch mode and batch setter exists, use it
      if (currentMetadata.uploadMode === 'batch' && typeof onApplyTrackNameToBatchPair === 'function') {
        onApplyTrackNameToBatchPair(chosenName);
        setSelectedTrackNameOptions(prev => [...prev, chosenName]);
        // Keep options open until all pairs are named
        // (Assume parent will move the checkmark to the next pair)
        // Optionally, close if all pairs are named (parent can pass count)
        return;
      } else if (typeof onApplyTrackNameToFirstPair === 'function') {
        // Single mode: update Track Name selector field in parent
        onApplyTrackNameToFirstPair(chosenName);
        addMessage({
          sender: 'bot',
          message: `Track name set to: ${chosenName}`,
        });
      } else {
        // Fallback: update metadata
        onApplyMetadata({ trackname: chosenName });
        addMessage({
          sender: 'bot',
          message: `Track name set to: ${chosenName}`,
        });
      }
    }
    // Only close options in single mode
    if (currentMetadata.uploadMode !== 'batch') {
      setShowTrackNameSelection(false);
      setTrackNameOptions([]);
    }
  };

  // When trackNameOptions changes, reset selected options
  useEffect(() => {
    setSelectedTrackNameOptions([]);
  }, [trackNameOptions]);

  const handleTrackNameDiscard = () => {
    addMessage({ sender: 'bot', message: 'No track name was selected.' });
    setShowTrackNameSelection(false);
    setTrackNameOptions([]);
  };

  // Update keydown handler for textarea
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <div className="chatbot-panel">
          <div className="chatbot-header">
            <h3>Vault Assistant</h3>
          </div>
          <div className="chatbot-messages">
            {messages.length === 0 ? (
              <div className="chatbot-welcome">
                <p>Please select the tracks you would like to upload, then I can help you name them, and add meta data.</p>
                <div className="capability-section">
                  <ul>
                    <li>"Name my track"</li>
                    <li>"...give me more name ideas, or try again"</li>
                    <li>"Apply this list of names to my tracks"</li>
                    <li>"Emotive orchestral track like Hanz Zimmer "</li>
                    <li>"Upbeat electronic dance track"</li>
                    <li>"Clear all"</li>

                  </ul>
                </div>
              </div>
            ) : (
              messages.map(msg => (
                <ChatMessage key={msg.id} message={msg.message} sender={msg.sender} />
              ))
            )}
            <div ref={messagesEndRef} />
            {showTrackNameSelection && (
              <SecondaryFilterSelection
                matches={trackNameOptions}
                onApply={handleTrackNameApply}
                onDiscard={handleTrackNameDiscard}
                hideCollection
                selectedOptions={selectedTrackNameOptions}
                keepOpen={currentMetadata.uploadMode === 'batch'}
              />
            )}
            {showAmbiguousSelection && (
              <SecondaryFilterSelection
                matches={ambiguousMatches}
                onApply={handleAmbiguousApply}
                onDiscard={handleAmbiguousDiscard}
              />
            )}
          </div>
          <div className="chatbot-input">
            <div className="input-row">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder={loading || audioAnalyzing ? 'Please wait...' : 'Describe your track...'}
                disabled={loading || audioAnalyzing}
                rows={1}
              />
              <div className="input-buttons">
                {/* Temporarily commented out - audio upload button for Add Track chatbot
                <button 
                  type="button"
                  className="audio-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || audioAnalyzing}
                  title="Upload audio file for analysis"
                >
                  {audioAnalyzing ? (
                    <i className="tim-icons icon-refresh-02 rotating"></i>
                  ) : (
                    <i className="tim-icons icon-upload" />
                  )}
                </button>
                */}
                <button onClick={handleSend} disabled={loading || audioAnalyzing || !input.trim()}>
                  {loading ? (
                    <i className="tim-icons icon-refresh-02 rotating"></i>
                  ) : (
                    <i className="tim-icons icon-send" />
                  )}
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.m4a"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}
      <ChatbotButton />
    </div>
  );
});

export default AddTrackChatbot; 